'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { logger } from '@/utils/logger';
import styles from '@/styles/Programs.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandHoldingHeart, faHandsHelping, faBuilding } from '@fortawesome/free-solid-svg-icons';

interface Program {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  alt_text: string | null;
  category: string | null;
  is_active: boolean | null;
  created_at: string;
  slug: string | null;
}

interface ProgramsClientProps {
  initialPrograms: Program[];
}

export default function ProgramsClient({ initialPrograms }: ProgramsClientProps) {
  const [programs, setPrograms] = useState<Program[]>(initialPrograms);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(Date.now());

  useEffect(() => {
    // Check if this is a browser refresh (no navigation state)
    const isBrowserRefresh = !window.performance.getEntriesByType?.('navigation')
      .some((nav: any) => nav.type === 'back_forward') && 
      window.performance.getEntriesByType?.('navigation')
      .some((nav: any) => nav.type === 'reload');

    // Always fetch on browser refresh, otherwise use cached data for 5 minutes
    const now = Date.now();
    const fiveMinutesAgo = now - 5 * 60 * 1000;

    if (isBrowserRefresh || !initialPrograms.length || (now - lastFetch) > fiveMinutesAgo) {
      fetchPrograms();
    }
  }, []);

  const fetchPrograms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('/api/programs');
      
      if (!response.ok) {
        throw new Error('Failed to fetch programs');
      }
      
      const data = await response.json();
      setPrograms(data.programs || []);
      setLastFetch(Date.now()); // Update last fetch timestamp
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      logger.error('Error fetching programs', { error: err });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Define category display order with priority
  const CATEGORY_ORDER: Record<string, number> = {
    'Education Initiatives': 1,
    'Community Outreach': 2,
    // Default priority for any other categories
    '_default': 999
  };

  // Get unique categories and sort them by priority
  const categories = programs
    .reduce<string[]>((acc, program) => {
      const category = program.category || 'Uncategorized';
      if (!acc.includes(category)) {
        acc.push(category);
      }
      return acc;
    }, [])
    .sort((a, b) => {
      const aPriority = CATEGORY_ORDER[a] || CATEGORY_ORDER['_default'];
      const bPriority = CATEGORY_ORDER[b] || CATEGORY_ORDER['_default'];
      if (aPriority !== bPriority) return aPriority - bPriority;
      return a.localeCompare(b);
    });
  
  // Group programs by category
  const programsByCategory = categories.reduce<Record<string, Program[]>>((acc, category) => {
    acc[category] = programs.filter(program => (program.category || 'Uncategorized') === category);
    return acc;
  }, {});

  if (isLoading && programs.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading programs...</div>
      </div>
    );
  }

  if (error && programs.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>Unable to load programs. Please try again later.</p>
          <button onClick={fetchPrograms} className="btn btn--primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <h1>Our Programs</h1>
          <p>Discover our programs and services designed to make a difference in our community.</p>
        </div>
      </section>

      {/* Programs Overview */}
      <section className={styles.overview}>
        <div className={styles.container}>
          <div className={styles.intro}>
            <p>
              At TimeLee Cares Foundation, we offer a range of programs focused on education, 
              community outreach, and personal development. Each program is designed to 
              provide comprehensive support and opportunities for growth.
            </p>
          </div>
        </div>
      </section>

      {/* Programs by Category */}
      {Object.entries(programsByCategory).map(([category, categoryPrograms]) => (
        <section key={category} className={styles.programCategory}>
          <div className={styles.container}>
            <h2>{category}</h2>
            <div className={styles.programsGrid}>
              {categoryPrograms.map((program) => {
                const isAcademicGuidance = program.title === 'Academic and Career Guidance';
                const programUrl = `/programs/${program.slug || program.id.toLowerCase().replace(/\s+/g, '-')}`;
                
                return (
                  <div key={program.id} className={styles.programCard}>
                    {program.image_url ? (
                      <img 
                        src={program.image_url} 
                        alt={program.alt_text || program.title}
                        className={styles.programImage}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            parent.style.background = '#f0f0f0';
                            parent.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 1rem;">No Image Available</div>';
                          }
                        }}
                        onLoad={(e) => {
                          e.currentTarget.style.display = 'block';
                        }}
                      />
                    ) : (
                      <div className={styles.programPlaceholder}>
                        No Image Available
                      </div>
                    )}
                    <div className={styles.programContent}>
                      <h3>{program.title}</h3>
                      {program.description && (
                        <p>{program.description}</p>
                      )}
                      {isAcademicGuidance ? (
                        <button 
                          onClick={() => window.location.href = programUrl}
                          className="btn btn--primary"
                          aria-label={`Learn more about ${program.title}`}
                          title={`Learn more about ${program.title}`}
                        >
                          Learn More
                        </button>
                      ) : (
                        <button 
                          className="btn btn--secondary" 
                          disabled
                          style={{ opacity: 0.6, cursor: 'not-allowed' }}
                          aria-label="Program coming soon"
                          title="This program will be available soon"
                        >
                          Coming Soon
                        </button>
                      )}
                    </div>
                    {isAcademicGuidance && (
                      <div 
                        onClick={() => window.location.href = programUrl}
                        className={styles.cardOverlay}
                        aria-label={`Learn more about ${program.title}`}
                        title={`Learn more about ${program.title}`}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Get Involved Section */}
      <section className={styles.getInvolved}>
        <div className={styles.container}>
          <h2>Get Involved</h2>
          <p>
            There are many ways to support our programs and make a difference in our community.
          </p>
          <div className={styles.involvedOptions}>
            <div className={styles.involvedCard}>
              <div className={styles.involvedIcon}>
                <FontAwesomeIcon icon={faHandHoldingHeart} />
              </div>
              <h3>Donate</h3>
              <p>Support our programs with a one-time or monthly donation.</p>
              <Link 
                href="/donate" 
                className="btn btn--primary" 
                aria-label="Navigate to donation page" 
                title="Make a donation to support TimeLee Cares Foundation"
              >
                Donate Now
              </Link>
            </div>
            <div className={styles.involvedCard}>
              <div className={styles.involvedIcon}>
                <FontAwesomeIcon icon={faHandsHelping} />
              </div>
              <h3>Volunteer</h3>
              <p>Share your time and talents to help with our programs.</p>
              <Link 
                href="/contact" 
                className="btn btn--primary" 
                aria-label="Contact us to volunteer" 
                title="Get in touch about volunteer opportunities"
              >
                Volunteer
              </Link>
            </div>
            <div className={styles.involvedCard}>
              <div className={styles.involvedIcon}>
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h3>Partner</h3>
              <p>Explore partnership opportunities for organizations.</p>
              <Link 
                href="/contact" 
                className="btn btn--primary" 
                aria-label="Contact us about partnership opportunities" 
                title="Get in touch to explore partnership opportunities"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
